import React, { useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import type { AddressCandidate, DestinationTemplate, ExportPreview, NormalizedAddressRecord } from "../shared/types";
import { formatDisplayDate, formatDriverLicenseForCopy, parseAamvaPayload, type DriverLicenseResult } from "./parsers/aamva";
import { startPdf417Scanner, type Pdf417ScannerSession } from "./scanners/pdf417Scanner";
import { normalizeTextToCandidates } from "../worker/services/normalize";
import "./styles/app.css";

const authStorageKey = "address_converter_admin";
const authRememberDays = 30;
const authRememberMs = authRememberDays * 24 * 60 * 60 * 1000;
type AccessRole = "admin" | "user";
type ScanMode = "address" | "driver_license";
type DriverLicenseScanStatus = "idle" | "starting" | "scanning" | "success" | "invalid" | "error";
type CropPercent = { x: number; y: number; width: number; height: number };
type CropHandle = "nw" | "ne" | "sw" | "se";

const defaultImageCrop: CropPercent = { x: 6, y: 38, width: 88, height: 28 };

const emptyRecord: NormalizedAddressRecord = {
  id: "",
  companyName: null,
  contactName: null,
  addressLine1: "",
  addressLine2: null,
  addressLine3: null,
  city: "",
  stateProvince: "",
  postalCode: "",
  countryCode: "US",
  phone: null,
  email: null,
  orderNumber: null,
  referenceNumber: null,
  trackingNumber: null,
  poNumber: null,
  addressType: "ship_to",
  residential: null,
  sourceType: "manual",
  sourceFileName: null,
  extractionConfidence: null,
  validationStatus: "unconfirmed"
};

function App() {
  const savedAuth = getSavedAuth();
  const [adminToken, setAdminToken] = useState(savedAuth.token);
  const [accessRole, setAccessRole] = useState<AccessRole | null>(savedAuth.role);
  const [rememberDevice, setRememberDevice] = useState(true);
  const [adminStatus, setAdminStatus] = useState<"unknown" | "valid" | "invalid">("unknown");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [rawText, setRawText] = useState("");
  const [markdown, setMarkdown] = useState("");
  const [candidates, setCandidates] = useState<AddressCandidate[]>([]);
  const [selectedCandidateId, setSelectedCandidateId] = useState("");
  const [record, setRecord] = useState<NormalizedAddressRecord>({ ...emptyRecord, id: crypto.randomUUID() });
  const [records, setRecords] = useState<NormalizedAddressRecord[]>([]);
  const [templates, setTemplates] = useState<DestinationTemplate[]>([]);
  const [templateId, setTemplateId] = useState("");
  const [selectedRecordIds, setSelectedRecordIds] = useState<string[]>([]);
  const [preview, setPreview] = useState<ExportPreview>();
  const [templateDraft, setTemplateDraft] = useState<DestinationTemplate>();
  const [copiedRecordId, setCopiedRecordId] = useState("");
  const [pendingImage, setPendingImage] = useState<{ file: File; url: string } | null>(null);
  const [imageCrop, setImageCrop] = useState<CropPercent>(defaultImageCrop);
  const [scanMode, setScanMode] = useState<ScanMode>("address");
  const [driverLicenseStatus, setDriverLicenseStatus] = useState<DriverLicenseScanStatus>("idle");
  const [driverLicenseResult, setDriverLicenseResult] = useState<DriverLicenseResult | null>(null);
  const [driverLicenseMessage, setDriverLicenseMessage] = useState("");
  const [driverLicenseCopied, setDriverLicenseCopied] = useState(false);
  const [driverLicenseCameraStatus, setDriverLicenseCameraStatus] = useState("");
  const [driverLicenseScannerSession, setDriverLicenseScannerSession] = useState<Pdf417ScannerSession | null>(null);
  const [torchEnabled, setTorchEnabled] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const cropImageRef = useRef<HTMLImageElement>(null);
  const csvInput = useRef<HTMLInputElement>(null);
  const templateInput = useRef<HTMLInputElement>(null);
  const selectAllInput = useRef<HTMLInputElement>(null);
  const driverLicenseVideoRef = useRef<HTMLVideoElement>(null);
  const driverLicenseSession = useRef<Pdf417ScannerSession | null>(null);
  const copiedTimeout = useRef<number>();
  const driverLicenseCopiedTimeout = useRef<number>();

  const selectedCandidate = useMemo(
    () => candidates.find((candidate) => candidate.id === selectedCandidateId),
    [candidates, selectedCandidateId]
  );

  useEffect(() => {
    void checkAdmin(adminToken, true);
    void fetch("/api/health")
      .then((response) => response.json() as Promise<{ ocrConfigured: boolean }>)
      .then((health) => {
        if (!health.ocrConfigured) setMessage("Mistral OCR is not configured. Text, manual, CSV, template, and export workflows are available.");
      })
      .catch(() => undefined);
    // Startup load should run once using the stored token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedCandidate) setRecord(selectedCandidate.record);
  }, [selectedCandidate]);

  useEffect(() => {
    if (!selectAllInput.current) return;
    selectAllInput.current.indeterminate = selectedRecordIds.length > 0 && selectedRecordIds.length < records.length;
  }, [records.length, selectedRecordIds.length]);

  useEffect(() => {
    return () => {
      if (copiedTimeout.current) window.clearTimeout(copiedTimeout.current);
      if (driverLicenseCopiedTimeout.current) window.clearTimeout(driverLicenseCopiedTimeout.current);
      stopDriverLicenseScanner();
      if (pendingImage) URL.revokeObjectURL(pendingImage.url);
    };
  }, [pendingImage]);

  async function beginDriverLicenseScan() {
    const video = driverLicenseVideoRef.current;
    if (!video || driverLicenseStatus === "starting" || driverLicenseStatus === "scanning") return;
    startDriverLicenseScan();
    const session = await startPdf417Scanner(video, {
      onDecode: (payload) => {
        const parsed = parseAamvaPayload(payload);
        if (!parsed) {
          setDriverLicenseStatus("invalid");
          setDriverLicenseMessage("Barcode detected, but it does not appear to be a supported driver's license.");
          setDriverLicenseScannerSession(null);
          return;
        }
        setDriverLicenseResult(parsed);
        setDriverLicenseStatus("success");
        setDriverLicenseMessage("");
        setDriverLicenseScannerSession(null);
        setTorchEnabled(false);
        if (navigator.vibrate) navigator.vibrate(80);
      },
      onError: (message) => {
        setDriverLicenseStatus("error");
        setDriverLicenseMessage(message);
        setDriverLicenseScannerSession(null);
      },
      onStarted: (status) => {
        setDriverLicenseCameraStatus(`Camera active: ${status}`);
        setDriverLicenseStatus("scanning");
      }
    });
    driverLicenseSession.current = session;
    setDriverLicenseScannerSession(session);
  }

  async function loadAll(token = adminToken) {
    const [recordResponse, templateResponse] = await Promise.all([apiFetch("/api/records", undefined, token), apiFetch("/api/templates", undefined, token)]);
    if (recordResponse.ok) {
      const body = (await recordResponse.json()) as { records: NormalizedAddressRecord[] };
      setRecords(body.records);
    }
    if (templateResponse.ok) {
      const body = (await templateResponse.json()) as { templates: DestinationTemplate[] };
      setTemplates(body.templates);
      setTemplateId((current) => current || body.templates[0]?.id || "");
    }
  }

  async function checkAdmin(token = adminToken, quiet = false) {
    if (!token.trim() && !quiet) return;
    setBusy(true);
    if (!quiet) setMessage("");
    try {
      const rememberHeaders = token.trim() ? { "X-Remember-Device": rememberDevice ? "true" : "false" } : undefined;
      const response = await apiFetch("/api/session", rememberHeaders ? { headers: rememberHeaders } : undefined, token);
      if (response.ok) {
        const body = (await response.json()) as { role: AccessRole };
        setAdminStatus("valid");
        setAccessRole(body.role);
        if (token.trim()) {
          if (rememberDevice) saveRememberedAuth(token, body.role);
          else localStorage.removeItem(authStorageKey);
        }
        if (!quiet) setMessage(body.role === "admin" ? "Admin access granted." : "User access granted.");
        await loadAll(token);
      } else if (response.status === 401) {
        setAdminStatus("invalid");
        if (!token.trim()) setAccessRole(null);
        if (!quiet) setMessage("Password is invalid.");
      } else {
        throw await apiError(response);
      }
    } catch (error) {
      setAdminStatus("invalid");
      if (!quiet) setMessage(error instanceof Error ? error.message : "Password check failed.");
    } finally {
      setBusy(false);
    }
  }

  function logoutAdmin() {
    void fetch("/api/logout", { method: "POST" }).catch(() => undefined);
    clearDriverLicenseResult();
    setAdminToken("");
    setAccessRole(null);
    setAdminStatus("unknown");
    localStorage.removeItem(authStorageKey);
    setRecords([]);
    setTemplates([]);
    setSelectedRecordIds([]);
    setPreview(undefined);
    setMessage("");
  }

  function selectScanMode(mode: ScanMode) {
    if (mode === "address") {
      clearDriverLicenseResult();
      setScanMode("address");
      return;
    }
    resetCaptureState();
    setMessage("");
    setScanMode("driver_license");
  }

  function startDriverLicenseScan() {
    stopDriverLicenseScanner();
    setDriverLicenseResult(null);
    setDriverLicenseMessage("");
    setDriverLicenseCopied(false);
    setDriverLicenseCameraStatus("");
    setTorchEnabled(false);
    setDriverLicenseStatus("starting");
  }

  function clearDriverLicenseResult() {
    stopDriverLicenseScanner();
    setDriverLicenseResult(null);
    setDriverLicenseMessage("");
    setDriverLicenseCopied(false);
    setDriverLicenseCameraStatus("");
    setTorchEnabled(false);
    setDriverLicenseStatus("idle");
  }

  function stopDriverLicenseScanner() {
    driverLicenseSession.current?.stop();
    driverLicenseSession.current = null;
    setDriverLicenseScannerSession(null);
  }

  async function analyzeText() {
    if (!rawText.trim()) return;
    setBusy(true);
    setMessage("");
    try {
      const response = await apiFetch("/api/analyze-text", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: rawText, sourceType: "pasted_text" })
      });
      if (!response.ok) throw await apiError(response);
      const body = (await response.json()) as { markdown: string; candidates: AddressCandidate[]; selectedCandidateId: string | null };
      const candidates = preferClientParsedCandidates(body.candidates, rawText);
      setMarkdown(body.markdown);
      setCandidates(candidates);
      setSelectedCandidateId(candidates[0]?.id ?? body.selectedCandidateId ?? "");
      if (!candidates.length) setMessage("No complete address was detected. Enter or correct the address manually.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Text analysis failed.");
    } finally {
      setBusy(false);
    }
  }

  function clearOcrText() {
    resetCaptureState();
    setMessage("");
  }

  function resetCaptureState() {
    setRawText("");
    setMarkdown("");
    setCandidates([]);
    setSelectedCandidateId("");
    setRecord({ ...emptyRecord, id: crypto.randomUUID() });
    setPendingImage((current) => {
      if (current) URL.revokeObjectURL(current.url);
      return null;
    });
    setImageCrop(defaultImageCrop);
    if (fileInput.current) fileInput.current.value = "";
  }

  function selectCaptureFile(file?: File) {
    if (!file) return;
    if (file.type === "application/pdf") {
      void analyzeFile(file);
      return;
    }
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setMessage("Upload a JPG, PNG, WebP, or PDF file.");
      return;
    }
    setPendingImage((current) => {
      if (current) URL.revokeObjectURL(current.url);
      return { file, url: URL.createObjectURL(file) };
    });
    setImageCrop(defaultImageCrop);
    setMarkdown("");
    setRawText("");
    setCandidates([]);
    setSelectedCandidateId("");
    setMessage("Adjust the selection around the address, then upload.");
  }

  async function analyzeFile(file?: File) {
    if (!file) return;
    setBusy(true);
    setMessage("");
    try {
      const form = new FormData();
      form.append("file", file);
      const response = await apiFetch("/api/ocr", { method: "POST", body: form });
      if (!response.ok) throw await apiError(response);
      const body = (await response.json()) as { markdown: string; candidates: AddressCandidate[]; selectedCandidateId: string | null };
      const candidates = preferClientParsedCandidates(body.candidates, body.markdown);
      setMarkdown(body.markdown);
      setRawText(body.markdown);
      setCandidates(candidates);
      setSelectedCandidateId(candidates[0]?.id ?? body.selectedCandidateId ?? "");
      if (!candidates.length) setMessage("OCR completed, but no complete address was detected.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "OCR failed.");
    } finally {
      setBusy(false);
    }
  }

  async function uploadSelectedCrop() {
    if (!pendingImage) return;
    setBusy(true);
    setMessage("");
    try {
      const blob = await renderImageCrop();
      const croppedFile = new File([blob], croppedFileName(pendingImage.file.name), { type: "image/jpeg" });
      setBusy(false);
      await analyzeFile(croppedFile);
      setPendingImage((current) => {
        if (current) URL.revokeObjectURL(current.url);
        return null;
      });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to crop image.");
      setBusy(false);
    }
  }

  async function renderImageCrop(): Promise<Blob> {
    const image = cropImageRef.current;
    if (!image || image.naturalWidth === 0 || image.naturalHeight === 0) throw new Error("Image is not ready.");
    const source = {
      x: (imageCrop.x / 100) * image.naturalWidth,
      y: (imageCrop.y / 100) * image.naturalHeight,
      width: (imageCrop.width / 100) * image.naturalWidth,
      height: (imageCrop.height / 100) * image.naturalHeight
    };
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(source.width));
    canvas.height = Math.max(1, Math.round(source.height));
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Unable to prepare cropped image.");
    context.drawImage(image, source.x, source.y, source.width, source.height, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.84));
    if (!blob) throw new Error("Unable to export cropped image.");
    return blob;
  }

  function beginCropMove(event: React.PointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    const bounds = event.currentTarget.parentElement?.getBoundingClientRect();
    if (!bounds) return;
    const startX = event.clientX;
    const startY = event.clientY;
    const startCrop = imageCrop;
    event.currentTarget.setPointerCapture(event.pointerId);

    const move = (nextEvent: PointerEvent) => {
      const dx = ((nextEvent.clientX - startX) / bounds.width) * 100;
      const dy = ((nextEvent.clientY - startY) / bounds.height) * 100;
      setImageCrop(clampCrop({ ...startCrop, x: startCrop.x + dx, y: startCrop.y + dy }));
    };
    const stop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
  }

  function beginCropResize(event: React.PointerEvent<HTMLButtonElement>, handle: CropHandle) {
    event.preventDefault();
    event.stopPropagation();
    const bounds = event.currentTarget.parentElement?.parentElement?.getBoundingClientRect();
    if (!bounds) return;
    const startX = event.clientX;
    const startY = event.clientY;
    const startCrop = imageCrop;
    event.currentTarget.setPointerCapture(event.pointerId);

    const move = (nextEvent: PointerEvent) => {
      const dx = ((nextEvent.clientX - startX) / bounds.width) * 100;
      const dy = ((nextEvent.clientY - startY) / bounds.height) * 100;
      const next = { ...startCrop };
      if (handle.includes("e")) next.width = startCrop.width + dx;
      if (handle.includes("s")) next.height = startCrop.height + dy;
      if (handle.includes("w")) {
        next.x = startCrop.x + dx;
        next.width = startCrop.width - dx;
      }
      if (handle.includes("n")) {
        next.y = startCrop.y + dy;
        next.height = startCrop.height - dy;
      }
      setImageCrop(clampCrop(next));
    };
    const stop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
  }

  async function makeAiFix() {
    if (!rawText.trim()) return;
    setBusy(true);
    setMessage("");
    try {
      const response = await apiFetch("/api/ai-fix-address", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: rawText })
      });
      if (!response.ok) throw await apiError(response);
      const body = (await response.json()) as { candidates: AddressCandidate[]; selectedCandidateId: string | null };
      setCandidates(body.candidates);
      setSelectedCandidateId(body.candidates[0]?.id ?? body.selectedCandidateId ?? "");
      if (body.candidates[0]) {
        setRecord(body.candidates[0].record);
        setMessage("AI fix found an address. Please review it.");
      } else {
        setMessage("AI fix could not find a complete address.");
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "AI address fix failed.");
    } finally {
      setBusy(false);
    }
  }

  async function normalizeCsv(file?: File) {
    if (!file) return;
    setBusy(true);
    setMessage("");
    try {
      const response = await apiFetch("/api/normalize-csv", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ csv: await file.text(), sourceFileName: file.name })
      });
      if (!response.ok) throw await apiError(response);
      const body = (await response.json()) as { records: NormalizedAddressRecord[] };
      if (body.records[0]) setRecord({ ...body.records[0], validationStatus: "unconfirmed" });
      setMessage(`Loaded ${body.records.length} CSV rows. Review and save records one at a time.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "CSV import failed.");
    } finally {
      setBusy(false);
    }
  }

  async function proposeTemplate(file?: File) {
    if (!file) return;
    setBusy(true);
    try {
      const response = await apiFetch("/api/templates/propose", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ csv: await file.text() })
      });
      if (!response.ok) throw await apiError(response);
      setTemplateDraft((await response.json()) as DestinationTemplate);
      setMessage("Template mapping proposed. Review field mappings and save it before export.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Template analysis failed.");
    } finally {
      setBusy(false);
    }
  }

  async function saveCurrentRecord() {
    const next = {
      ...record,
      id: record.id || crypto.randomUUID(),
      validationStatus: record.validationStatus === "corrected" ? "corrected" : "confirmed"
    } satisfies NormalizedAddressRecord;
    setBusy(true);
    try {
      const response = await apiFetch("/api/records", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ record: next, rawText: markdown || rawText })
      });
      if (!response.ok) throw await apiError(response);
      await loadAll();
      resetCaptureState();
      setMessage("Confirmed address saved.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Save failed.");
    } finally {
      setBusy(false);
    }
  }

  async function saveTemplateDraft() {
    if (!templateDraft) return;
    const response = await apiFetch("/api/templates", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(templateDraft)
    });
    if (!response.ok) {
      setMessage((await apiError(response)).message);
      return;
    }
    await loadAll();
    setTemplateDraft(undefined);
    setMessage("Template saved. Future exports use the stored mapping deterministically.");
  }

  async function buildPreview(download = false) {
    if (!templateId || !selectedRecordIds.length) return;
    const response = await apiFetch(download ? "/api/export/download" : "/api/export/preview", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ templateId, recordIds: selectedRecordIds })
    });
    if (!response.ok) {
      setMessage((await apiError(response)).message);
      return;
    }
    if (download) {
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = response.headers.get("content-disposition")?.match(/filename="([^"]+)"/)?.[1] ?? "export.csv";
      anchor.click();
      URL.revokeObjectURL(url);
      return;
    }
    setPreview((await response.json()) as ExportPreview);
  }

  async function deleteSavedRecord(id: string) {
    await apiFetch(`/api/records/${id}`, { method: "DELETE" });
    setSelectedRecordIds((current) => current.filter((item) => item !== id));
    await loadAll();
  }

  async function copySavedRecord(saved: NormalizedAddressRecord) {
    const text = formatConfirmedRecord(saved);
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
      } else {
        copyTextFallback(text);
      }
      setCopiedRecordId(saved.id);
      if (copiedTimeout.current) window.clearTimeout(copiedTimeout.current);
      copiedTimeout.current = window.setTimeout(() => setCopiedRecordId(""), 1000);
      setMessage("Record copied.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Copy failed.");
    }
  }

  async function copyDriverLicenseData() {
    if (!driverLicenseResult) return;
    const text = formatDriverLicenseForCopy(driverLicenseResult);
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
      } else {
        copyTextFallback(text);
      }
      setDriverLicenseCopied(true);
      if (driverLicenseCopiedTimeout.current) window.clearTimeout(driverLicenseCopiedTimeout.current);
      driverLicenseCopiedTimeout.current = window.setTimeout(() => setDriverLicenseCopied(false), 1000);
      setDriverLicenseMessage("Driver License data copied.");
    } catch (error) {
      setDriverLicenseMessage(error instanceof Error ? error.message : "Copy failed.");
    }
  }

  async function toggleTorch() {
    const next = !torchEnabled;
    try {
      await driverLicenseScannerSession?.setTorch?.(next);
      setTorchEnabled(next);
    } catch {
      setDriverLicenseMessage("Torch is not available on this camera.");
    }
  }

  function updateField<K extends keyof NormalizedAddressRecord>(field: K, value: NormalizedAddressRecord[K]) {
    setRecord((current) => ({ ...current, [field]: value, validationStatus: "corrected" }));
  }

  function pasteAddressIntoForm(text: string): boolean {
    const [candidate] = normalizeTextToCandidates(text, "manual");
    if (!candidate) return false;
    setRecord((current) => ({
      ...current,
      ...candidate.record,
      id: current.id || candidate.record.id,
      sourceType: current.sourceType,
      sourceFileName: current.sourceFileName,
      validationStatus: "corrected"
    }));
    setMessage("Pasted address split into fields. Review before confirming.");
    return true;
  }

  function apiFetch(input: string, init: RequestInit = {}, token = adminToken) {
    const headers = new Headers(init.headers);
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return fetch(input, { ...init, headers });
  }

  const canConfirm = record.addressLine1 && record.city && record.stateProvince && record.postalCode;
  const hasOcrText = Boolean(rawText.trim());
  const hasPendingImage = Boolean(pendingImage);
  const hasCaptureInput = hasOcrText || hasPendingImage;
  const canMakeAiFix = hasOcrText && !isCompleteAddress(candidates[0]?.record);
  const allRecordsSelected = records.length > 0 && selectedRecordIds.length === records.length;
  const addressLines = formatAddress(record);
  const mapsUrl = googleMapsUrl(record);
  const mapsEmbedUrl = googleMapsEmbedUrl(record);
  const loginUrl = window.location.origin;
  const loginQrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=112x112&margin=8&data=${encodeURIComponent(loginUrl)}`;

  if (adminStatus !== "valid") {
    return (
      <main className="frontDoor">
        <a className="mobileLoginQr" href={loginUrl} aria-label="Open mobile login URL">
          <img src={loginQrUrl} alt="" />
        </a>
        <form
          className="frontDoorPanel"
          onSubmit={(event) => {
            event.preventDefault();
            void checkAdmin();
          }}
        >
          <div>
            <p className="eyebrow">Address Template Converter</p>
            <h1>Enter password</h1>
          </div>
          <label>
            Password
            <input
              autoFocus
              value={adminToken}
              onChange={(event) => {
                setAdminToken(event.target.value);
                setAdminStatus("unknown");
              }}
              placeholder="Password"
              type="password"
            />
          </label>
          <label className="rememberDevice">
            <input type="checkbox" checked={rememberDevice} onChange={(event) => setRememberDevice(event.target.checked)} />
            <span>Remember this device for 30 days</span>
          </label>
          <button type="submit" disabled={busy || !adminToken.trim()}>
            {busy ? "Checking..." : "Continue"}
          </button>
          {message && <div className="notice">{message}</div>}
        </form>
      </main>
    );
  }

  return (
    <main className="shell">
      <header className="topbar">
        <div>
          <p className="eyebrow">Address Template Converter</p>
          <h1>Review, confirm, export</h1>
        </div>
        <div className="sessionActions">
          <a className="headerLoginQr" href={loginUrl} aria-label="Open mobile login URL">
            <img src={loginQrUrl} alt="" />
          </a>
          <button type="button" className="secondary compact" onClick={logoutAdmin}>
            Log out
          </button>
        </div>
      </header>

      {message && <div className="notice">{message}</div>}

      <section className="modePanel" aria-label="Scan mode">
        <p className="modePrompt">What do you want to scan?</p>
        <fieldset className="modeRadios">
          <legend className="srOnly">Scan mode</legend>
          <label className={scanMode === "address" ? "modeRadio active" : "modeRadio"}>
            <input type="radio" name="scanMode" value="address" checked={scanMode === "address"} onChange={() => selectScanMode("address")} />
            <span>Address</span>
          </label>
          <label className={scanMode === "driver_license" ? "modeRadio active" : "modeRadio"}>
            <input
              type="radio"
              name="scanMode"
              value="driver_license"
              checked={scanMode === "driver_license"}
              onChange={() => selectScanMode("driver_license")}
            />
            <span>Driver License</span>
          </label>
        </fieldset>
      </section>

      {scanMode === "driver_license" ? (
        <DriverLicensePanel
          copied={driverLicenseCopied}
          cameraStatus={driverLicenseCameraStatus}
          message={driverLicenseMessage}
          result={driverLicenseResult}
          status={driverLicenseStatus}
          torchAvailable={Boolean(driverLicenseScannerSession?.setTorch)}
          torchEnabled={torchEnabled}
          onBack={() => selectScanMode("address")}
          onClear={clearDriverLicenseResult}
          onCopy={() => void copyDriverLicenseData()}
          onScanAnother={driverLicenseResult ? clearDriverLicenseResult : () => void beginDriverLicenseScan()}
          onToggleTorch={() => void toggleTorch()}
          videoRef={driverLicenseVideoRef}
        />
      ) : (
      <>
      <section className="workspace">
        <div className="capture">
          <div className="actions">
            <button type="button" onClick={() => fileInput.current?.click()} disabled={busy || hasCaptureInput}>
              <span className="cameraButtonIcon" aria-hidden="true" />
              <span>Capture Photo</span>
            </button>
            <button type="button" className="secondary" onClick={() => csvInput.current?.click()} disabled={busy}>
              Source CSV
            </button>
            <button type="button" className={hasOcrText ? "normalizeButton" : "secondary"} onClick={analyzeText} disabled={busy || !hasOcrText}>
              Normalize Text
            </button>
            <button type="button" className="aiFixButton" onClick={() => void makeAiFix()} disabled={busy || !canMakeAiFix}>
              <span className="sparkleIcon" aria-hidden="true">✧</span>
              <span>Make AI Fix</span>
            </button>
          </div>
          <input
            ref={fileInput}
            hidden
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            capture="environment"
            onChange={(event) => selectCaptureFile(event.target.files?.[0])}
          />
          <input ref={csvInput} hidden type="file" accept=".csv,text/csv" onChange={(event) => void normalizeCsv(event.target.files?.[0])} />
          {pendingImage && (
            <section className="cropPanel" aria-label="Image crop selection">
              <div className="cropPreview">
                <img ref={cropImageRef} src={pendingImage.url} alt="Selected upload" />
                <div
                  className="cropBox"
                  style={{ left: `${imageCrop.x}%`, top: `${imageCrop.y}%`, width: `${imageCrop.width}%`, height: `${imageCrop.height}%` }}
                  onPointerDown={beginCropMove}
                >
                  <CropHandleButton position="topLeft" onPointerDown={(event) => beginCropResize(event, "nw")} />
                  <CropHandleButton position="topRight" onPointerDown={(event) => beginCropResize(event, "ne")} />
                  <CropHandleButton position="bottomLeft" onPointerDown={(event) => beginCropResize(event, "sw")} />
                  <CropHandleButton position="bottomRight" onPointerDown={(event) => beginCropResize(event, "se")} />
                </div>
              </div>
              <div className="cropActions">
                <button type="button" className="secondary" onClick={() => setImageCrop(defaultImageCrop)} disabled={busy}>
                  Reset Selection
                </button>
                <button type="button" className="secondary" onClick={clearOcrText} disabled={busy}>
                  Cancel
                </button>
                <button type="button" onClick={() => void uploadSelectedCrop()} disabled={busy}>
                  Upload Selection
                </button>
              </div>
            </section>
          )}
          <label>
            Pasted address or OCR text
            <textarea value={rawText} onChange={(event) => setRawText(event.target.value)} placeholder="Paste unstructured address text..." />
          </label>
          <div className="textActions">
            <button type="button" className="secondary" onClick={clearOcrText} disabled={busy || !hasCaptureInput}>
              Clear Text
            </button>
          </div>
          {candidates.length > 0 && (
            <label>
              Candidate
              <select value={selectedCandidateId} onChange={(event) => setSelectedCandidateId(event.target.value)}>
                {candidates.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.record.addressLine1}, {candidate.record.city} ({Math.round(candidate.confidence * 100)}%)
                  </option>
                ))}
              </select>
            </label>
          )}
          {selectedCandidate && (
            <div className={selectedCandidate.confidence < 0.7 ? "confidence low" : "confidence"}>
              Confidence {Math.round(selectedCandidate.confidence * 100)}%
              {selectedCandidate.warnings.length > 0 && <p>{selectedCandidate.warnings.join(" ")}</p>}
            </div>
          )}
          {markdown && (
            <details className="raw">
              <summary>OCR / source text</summary>
              <pre>{markdown}</pre>
            </details>
          )}
        </div>

        <form className="form" onSubmit={(event) => event.preventDefault()}>
          <section className="extractedAddress">
            <div className="reviewHeader">
              <h2>Extracted Address</h2>
              {record.extractionConfidence !== null && (
                <div className={record.extractionConfidence < 0.7 ? "confidenceBadge low" : "confidenceBadge"}>
                  <span>Confidence</span>
                  <strong>{Math.round(record.extractionConfidence * 100)}%</strong>
                </div>
              )}
            </div>
            <address>
              {addressLines.length ? (
                addressLines.map((line) => <span key={line}>{line}</span>)
              ) : (
                <span className="placeholderText">No address selected</span>
              )}
            </address>
            {mapsUrl && mapsEmbedUrl && (
              <a className="mapPreview" href={mapsUrl} target="_blank" rel="noreferrer" aria-label="Open address in Google Maps">
                <iframe title="Google Maps preview" src={mapsEmbedUrl} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </a>
            )}
            <div className="reviewActions">
              <a className={mapsUrl ? "buttonLink secondary" : "buttonLink secondary disabled"} href={mapsUrl || undefined} target="_blank" rel="noreferrer">
                Verify in Google Maps
              </a>
            </div>
          </section>
              <AddressFields record={record} updateField={updateField} onAddressPaste={pasteAddressIntoForm} />
          <div className="actions">
            <button type="button" onClick={() => void saveCurrentRecord()} disabled={!canConfirm || busy}>
              Confirm Address
            </button>
            <button type="button" className="secondary" onClick={() => setRecord({ ...emptyRecord, id: crypto.randomUUID() })} disabled={busy}>
              Manual Entry
            </button>
          </div>
        </form>
      </section>

      <section className="history">
        <div className="historyHeader">
          <h2>Destination Templates</h2>
          {accessRole === "admin" && (
            <>
              <button type="button" className="secondary compact" onClick={() => templateInput.current?.click()} disabled={busy}>
                Import Template
              </button>
              <input ref={templateInput} hidden type="file" accept=".csv,text/csv" onChange={(event) => void proposeTemplate(event.target.files?.[0])} />
            </>
          )}
        </div>
        {accessRole === "admin" && templateDraft && (
          <div className="templateEditor">
            <label>
              Template name
              <input value={templateDraft.name} onChange={(event) => setTemplateDraft({ ...templateDraft, name: event.target.value })} />
            </label>
            <div className="mappingGrid">
              {templateDraft.headers.map((header) => (
                <label key={header}>
                  {header}
                  <input
                    value={templateDraft.mapping[header] ?? ""}
                    onChange={(event) =>
                      setTemplateDraft({ ...templateDraft, mapping: { ...templateDraft.mapping, [header]: event.target.value } })
                    }
                  />
                </label>
              ))}
            </div>
            <button type="button" onClick={() => void saveTemplateDraft()}>
              Save Template
            </button>
          </div>
        )}
      </section>

      <section className="history">
        <div className="historyHeader">
          <h2>Confirmed Records</h2>
          <div className="exportControls">
            <select value={templateId} onChange={(event) => setTemplateId(event.target.value)}>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name} v{template.version}
                </option>
              ))}
            </select>
            <button type="button" onClick={() => void buildPreview(true)} disabled={!selectedRecordIds.length}>
              Export CSV
            </button>
          </div>
        </div>
        <label className="selectAll">
          <input
            ref={selectAllInput}
            type="checkbox"
            checked={allRecordsSelected}
            disabled={!records.length}
            onChange={(event) => setSelectedRecordIds(event.target.checked ? records.map((saved) => saved.id) : [])}
          />
          <span>Select</span>
        </label>
        {records.map((saved) => (
          <article key={saved.id} className="historyItem">
            <label className="check">
              <input
                type="checkbox"
                checked={selectedRecordIds.includes(saved.id)}
                onChange={(event) =>
                  setSelectedRecordIds((current) => (event.target.checked ? [...current, saved.id] : current.filter((id) => id !== saved.id)))
                }
              />
              <span>
                <strong>{saved.contactName || saved.companyName || saved.addressLine1}</strong>
                <p>
                  {saved.addressLine1}, {saved.city}, {saved.stateProvince} {saved.postalCode}
                </p>
              </span>
            </label>
            <div className="historyActions">
              <button type="button" className={copiedRecordId === saved.id ? "iconButton copied" : "iconButton"} onClick={() => void copySavedRecord(saved)}>
                {copiedRecordId === saved.id ? "Copied" : "Copy"}
              </button>
              {accessRole === "admin" && (
                <button type="button" className="iconButton danger" onClick={() => void deleteSavedRecord(saved.id)}>
                  Delete
                </button>
              )}
            </div>
          </article>
        ))}
        {preview && (
          <div className="previewCsv">
            {preview.errors.length > 0 && <div className="notice">{preview.errors.join("\n")}</div>}
            <pre>{preview.csv}</pre>
          </div>
        )}
      </section>
      </>
      )}
    </main>
  );
}

function DriverLicensePanel({
  copied,
  cameraStatus,
  message,
  result,
  status,
  torchAvailable,
  torchEnabled,
  videoRef,
  onBack,
  onClear,
  onCopy,
  onScanAnother,
  onToggleTorch
}: {
  copied: boolean;
  cameraStatus: string;
  message: string;
  result: DriverLicenseResult | null;
  status: DriverLicenseScanStatus;
  torchAvailable: boolean;
  torchEnabled: boolean;
  videoRef: React.RefObject<HTMLVideoElement>;
  onBack: () => void;
  onClear: () => void;
  onCopy: () => void;
  onScanAnother: () => void;
  onToggleTorch: () => void;
}) {
  const scanning = status === "starting" || status === "scanning";

  return (
    <section className="driverLicensePanel" aria-label="Driver License scanner">
      <div className="driverLicenseHeader">
        <div>
          <p className="eyebrow">Driver License</p>
          <h2>{result ? "Scanned information" : "Point camera at barcode on back of license"}</h2>
        </div>
        <p className="privacyNote">Processed locally - scan is not saved</p>
      </div>

      {!result && (
        <div className={status === "invalid" || status === "error" ? "scannerStage paused" : "scannerStage"}>
          <video ref={videoRef} className="scannerVideo" playsInline muted autoPlay />
          <div className="scanGuide" aria-hidden="true">
            <span />
          </div>
          <div className="scannerOverlayText">{scanning ? "Scanning..." : "Ready to scan"}</div>
        </div>
      )}

      {cameraStatus && !result && <p className="cameraStatus">{cameraStatus}</p>}

      {message && <div className={status === "invalid" || status === "error" ? "notice scannerNotice" : "notice"}>{message}</div>}

      {result ? (
        <DriverLicenseResultView result={result} />
      ) : (
        <div className="driverLicenseActions">
          {(status === "idle" || status === "invalid" || status === "error") && (
            <button type="button" onClick={onScanAnother}>
              {status === "idle" ? "Scan Now" : "Retry"}
            </button>
          )}
          {torchAvailable && scanning && (
            <button type="button" className="secondary" onClick={onToggleTorch}>
              {torchEnabled ? "Torch Off" : "Torch On"}
            </button>
          )}
          <button type="button" className="secondary" onClick={onBack}>
            Cancel
          </button>
        </div>
      )}

      {result && (
        <div className="driverLicenseActions">
          <button type="button" onClick={onCopy}>
            {copied ? "Copied" : "Copy Data"}
          </button>
          <button type="button" className="secondary" onClick={onScanAnother}>
            Scan Another
          </button>
          <button type="button" className="secondary" onClick={onClear}>
            Clear
          </button>
        </div>
      )}
    </section>
  );
}

function DriverLicenseResultView({ result }: { result: DriverLicenseResult }) {
  const fullName = [result.firstName, result.middleName, result.lastName].filter(Boolean).join(" ");
  const cityLine = [result.address?.city, [result.address?.state, result.address?.postalCode].filter(Boolean).join(" ")].filter(Boolean).join(", ");

  return (
    <section className="driverLicenseResult" aria-label="Driver License result">
      <h2>Driver License</h2>
      <div className="licenseFields">
        {fullName && <LicenseValue label="Name" value={fullName} />}
        {result.licenseNumber && <LicenseValue label="License #" value={result.licenseNumber} />}
        {result.dateOfBirth && <LicenseValue label="DOB" value={formatDisplayDate(result.dateOfBirth)} />}
        {result.expirationDate && <LicenseValue label="Expiration" value={formatDisplayDate(result.expirationDate)} />}
        {(result.address?.street || cityLine) && (
          <div className="licenseValue wide">
            <span>Address</span>
            <strong>
              {result.address?.street && <span>{result.address.street}</span>}
              {cityLine && <span>{cityLine}</span>}
            </strong>
          </div>
        )}
        {result.issueDate && <LicenseValue label="Issue Date" value={formatDisplayDate(result.issueDate)} />}
        {result.sex && <LicenseValue label="Sex" value={result.sex} />}
        {result.height && <LicenseValue label="Height" value={result.height} />}
        {result.issuingCountry && <LicenseValue label="Country" value={result.issuingCountry} />}
        {result.aamvaVersion && <LicenseValue label="AAMVA" value={`Version ${result.aamvaVersion}`} />}
      </div>
    </section>
  );
}

function LicenseValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="licenseValue">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function AddressFields({
  record,
  updateField,
  onAddressPaste
}: {
  record: NormalizedAddressRecord;
  updateField: <K extends keyof NormalizedAddressRecord>(field: K, value: NormalizedAddressRecord[K]) => void;
  onAddressPaste: (text: string) => boolean;
}) {
  function handlePaste(event: React.ClipboardEvent<HTMLInputElement>) {
    const text = event.clipboardData.getData("text");
    if (!text.trim() || !onAddressPaste(text)) return;
    event.preventDefault();
  }

  return (
    <>
      <Field label="Contact" value={record.contactName ?? ""} onPaste={handlePaste} onChange={(value) => updateField("contactName", value || null)} />
      <Field label="Company" value={record.companyName ?? ""} onPaste={handlePaste} onChange={(value) => updateField("companyName", value || null)} />
      <Field
        name="addressLine1"
        label="Address line 1"
        required
        value={record.addressLine1}
        onPaste={handlePaste}
        onChange={(value) => updateField("addressLine1", value)}
      />
      <Field label="Address line 2" value={record.addressLine2 ?? ""} onPaste={handlePaste} onChange={(value) => updateField("addressLine2", value || null)} />
      <div className="grid">
        <Field label="City" required value={record.city} onPaste={handlePaste} onChange={(value) => updateField("city", value)} />
        <Field label="State" required value={record.stateProvince} onPaste={handlePaste} onChange={(value) => updateField("stateProvince", value.toUpperCase())} />
        <Field label="Postal" required value={record.postalCode} onPaste={handlePaste} onChange={(value) => updateField("postalCode", value)} />
      </div>
      <div className="grid two">
        <Field label="Country" value={record.countryCode} onChange={(value) => updateField("countryCode", value.toUpperCase().slice(0, 2))} />
        <Field label="Phone" value={record.phone ?? ""} onChange={(value) => updateField("phone", value || null)} />
      </div>
      <Field label="Email" value={record.email ?? ""} onChange={(value) => updateField("email", value || null)} />
      <div className="grid two">
        <Field label="Order" value={record.orderNumber ?? ""} onChange={(value) => updateField("orderNumber", value || null)} />
        <Field label="Reference" value={record.referenceNumber ?? ""} onChange={(value) => updateField("referenceNumber", value || null)} />
      </div>
    </>
  );
}

function Field({
  label,
  value,
  onChange,
  required = false,
  name,
  onPaste
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  name?: string;
  onPaste?: (event: React.ClipboardEvent<HTMLInputElement>) => void;
}) {
  return (
    <label>
      {label}
      <input name={name} required={required} value={value} onPaste={onPaste} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function CropHandleButton({
  position,
  onPointerDown
}: {
  position: "topLeft" | "topRight" | "bottomLeft" | "bottomRight";
  onPointerDown: (event: React.PointerEvent<HTMLButtonElement>) => void;
}) {
  return <button type="button" aria-label="Resize crop selection" className={`cropHandle ${position}`} onPointerDown={onPointerDown} />;
}

function preferClientParsedCandidates(serverCandidates: AddressCandidate[], text: string): AddressCandidate[] {
  const clientCandidates = normalizeTextToCandidates(text, "pasted_text");
  if (!isCompleteAddress(clientCandidates[0]?.record)) return serverCandidates;
  if (!serverCandidates[0] || !isCompleteAddress(serverCandidates[0].record)) return clientCandidates;
  return clientCandidates[0].confidence >= serverCandidates[0].confidence ? clientCandidates : serverCandidates;
}

function isCompleteAddress(record?: NormalizedAddressRecord): boolean {
  return Boolean(record?.addressLine1 && record.city && record.stateProvince && record.postalCode);
}

function clampCrop(crop: CropPercent): CropPercent {
  const width = Math.max(18, Math.min(crop.width, 100));
  const height = Math.max(12, Math.min(crop.height, 100));
  return {
    width,
    height,
    x: Math.max(0, Math.min(crop.x, 100 - width)),
    y: Math.max(0, Math.min(crop.y, 100 - height))
  };
}

function croppedFileName(name: string): string {
  const stem = name.replace(/\.[^.]+$/, "") || "address";
  return `${stem}-selection.jpg`;
}

function formatAddress(record: NormalizedAddressRecord): string[] {
  const cityLine = [record.city, [record.stateProvince, record.postalCode].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return [record.contactName, record.companyName, record.addressLine1, record.addressLine2, record.addressLine3, cityLine, record.countryCode]
    .map((line) => line?.trim())
    .filter((line): line is string => Boolean(line));
}

function formatConfirmedRecord(record: NormalizedAddressRecord): string {
  return [record.companyName, record.contactName, record.addressLine1, record.city, [record.stateProvince, record.postalCode].filter(Boolean).join(" ")]
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value))
    .join(", ");
}

function copyTextFallback(text: string) {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  if (!copied) throw new Error("Copy failed.");
}

function getSavedAuth(): { token: string; role: AccessRole | null } {
  const saved = localStorage.getItem(authStorageKey);
  if (!saved) return { token: "", role: null };
  try {
    const parsed = JSON.parse(saved) as { token?: unknown; role?: unknown; expiresAt?: unknown };
    if (
      typeof parsed.token !== "string" ||
      (parsed.role !== "admin" && parsed.role !== "user") ||
      typeof parsed.expiresAt !== "number" ||
      parsed.expiresAt <= Date.now()
    ) {
      localStorage.removeItem(authStorageKey);
      return { token: "", role: null };
    }
    return { token: parsed.token, role: parsed.role };
  } catch {
    saveRememberedAuth(saved, "admin");
    return { token: saved, role: "admin" };
  }
}

function saveRememberedAuth(token: string, role: AccessRole) {
  localStorage.setItem(authStorageKey, JSON.stringify({ token, role, expiresAt: Date.now() + authRememberMs }));
}

function googleMapsUrl(record: NormalizedAddressRecord): string {
  const query = formatAddress(record).join(" ");
  return record.addressLine1 && record.city && record.stateProvince ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : "";
}

function googleMapsEmbedUrl(record: NormalizedAddressRecord): string {
  const query = formatAddress(record).join(" ");
  return record.addressLine1 && record.city && record.stateProvince ? `https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed` : "";
}

async function apiError(response: Response): Promise<Error> {
  const body = (await response.json().catch(() => undefined)) as { error?: { message?: string } } | undefined;
  return new Error(body?.error?.message ?? `Request failed with status ${response.status}`);
}

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
